'use client';

import { useState, useEffect } from 'react';
import LayoutDefault from '../../layouts/LayoutDefault';
import { commentsService, CommentObjectType, Comment, CommentFilters } from '../../utils/services/comments';
import { useToast } from '@/templates/Admin/components/Toast/ToastProvider';
import { SearchOutlined as Search, PlusOutlined as Plus, EditIcon as Edit, TrashIcon as Delete, CloseOutlined as Close, RefreshOutlined as Reload } from '../../utils/icons';
import { Button } from '@/templates/Admin/components/Button/Button';

// Icon components
const Message = ({ className = '' }: { className?: string }) => (
    <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
    </svg>
);

const Filter = ({ className = '' }: { className?: string }) => (
    <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
    </svg>
);

// Simple UI Components
const Input = ({ className = '', ...props }: React.InputHTMLAttributes<HTMLInputElement>) => <input className={`px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${className}`} {...props} />;

const Select = ({ className = '', children, ...props }: React.SelectHTMLAttributes<HTMLSelectElement>) => (
    <select className={`px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${className}`} {...props}>
        {children}
    </select>
);

const Textarea = ({ className = '', ...props }: React.TextareaHTMLAttributes<HTMLTextAreaElement>) => <textarea className={`px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${className}`} {...props} />;

const Modal = ({ isOpen, onClose, title, children }: { isOpen: boolean; onClose: () => void; title: string; children: React.ReactNode }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-4 border-b">
                    <h2 className="text-xl font-semibold">{title}</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <Close />
                    </button>
                </div>
                <div className="p-4">{children}</div>
            </div>
        </div>
    );
};

const Tabs = ({ activeTab, onChange, tabs }: { activeTab: string; onChange: (tab: string) => void; tabs: Array<{ key: string; label: string; icon?: React.ReactNode }> }) => (
    <div className="flex border-b mb-4">
        {tabs.map((tab) => (
            <button key={tab.key} onClick={() => onChange(tab.key)} className={`px-4 py-2 border-b-2 transition-colors flex items-center gap-2 ${activeTab === tab.key ? 'border-blue-500 bg-blue-50 text-blue-600' : 'border-transparent hover:border-blue-300 hover:bg-gray-50'}`}>
                {tab.icon}
                {tab.label}
            </button>
        ))}
    </div>
);

const StatCard = ({ title, value, icon, color = 'blue' }: { title: string; value: string | number; icon: React.ReactNode; color?: string }) => (
    <div className="bg-white rounded-lg shadow p-6 border-l-4" style={{ borderLeftColor: color }}>
        <div className="flex items-center justify-between">
            <div>
                <p className="text-sm text-gray-600 mb-1">{title}</p>
                <p className="text-2xl font-bold">{value}</p>
            </div>
            <div className={`text-${color}-500 text-3xl`}>{icon}</div>
        </div>
    </div>
);

interface CommentStatisticsProps {
    onClose: () => void;
    isSidebarOpen: boolean;
}

export default function CommentStatistics({ onClose, isSidebarOpen }: CommentStatisticsProps) {
    const { showSuccess, showError } = useToast();
    const [activeTab, setActiveTab] = useState('statistics');
    const [loadingStats, setLoadingStats] = useState(false);
    const [loadingComments, setLoadingComments] = useState(false);
    const [loadingTypes, setLoadingTypes] = useState(false);
    const [loadingAction, setLoadingAction] = useState(false);

    // Comment Object Types State
    const [commentObjectTypes, setCommentObjectTypes] = useState<CommentObjectType[]>([]);
    const [showTypeModal, setShowTypeModal] = useState(false);
    const [newTypeName, setNewTypeName] = useState('');
    const [newTypeDescription, setNewTypeDescription] = useState('');

    // Comments State
    const [comments, setComments] = useState<Comment[]>([]);
    const [selectedComment, setSelectedComment] = useState<Comment | null>(null);
    const [showCommentModal, setShowCommentModal] = useState(false);
    const [editingComment, setEditingComment] = useState<Comment | null>(null);

    // Filters State
    const [filters, setFilters] = useState<CommentFilters>({
        page: 1,
        limit: 10,
        status: '',
        comment_object_type_id: undefined,
        search: '',
        start_date: '',
        end_date: '',
    });
    const [pagination, setPagination] = useState({
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 0,
    });

    // Statistics State
    const [statistics, setStatistics] = useState<any>(null);

    useEffect(() => {
        loadCommentObjectTypes();
    }, []);

    useEffect(() => {
        if (activeTab === 'comments') {
            loadComments();
        } else if (activeTab === 'statistics') {
            loadStatistics();
        }
    }, [activeTab, filters]);

    const loadCommentObjectTypes = async () => {
        try {
            setLoadingTypes(true);
            const response = await commentsService.getAllCommentObjectTypes();
            if (response.success) {
                setCommentObjectTypes(response.data);
            }
        } catch (error) {
            showError('Không thể tải danh sách loại bình luận', 'Lỗi');
        } finally {
            setLoadingTypes(false);
        }
    };

    const loadComments = async () => {
        try {
            setLoadingComments(true);
            const response = await commentsService.getAllComments(filters);
            if (response.success) {
                setComments(response.data);
                setPagination(response.pagination);
            }
        } catch (error) {
            showError('Không thể tải danh sách bình luận', 'Lỗi');
        } finally {
            setLoadingComments(false);
        }
    };

    const loadStatistics = async () => {
        try {
            setLoadingStats(true);
            const response = await commentsService.getCommentStatistics({
                start_date: filters.start_date,
                end_date: filters.end_date,
                comment_object_type_id: filters.comment_object_type_id,
            });
            if (response.success) {
                setStatistics(response.data);
            }
        } catch (error) {
            showError('Không thể tải thống kê bình luận', 'Lỗi');
        } finally {
            setLoadingStats(false);
        }
    };

    const handleCreateCommentObjectType = async () => {
        if (!newTypeName.trim()) {
            showError('Vui lòng nhập tên loại bình luận', 'Lỗi');
            return;
        }

        try {
            setLoadingAction(true);
            const response = await commentsService.createCommentObjectType({
                name: newTypeName,
                description: newTypeDescription,
            });
            if (response.success) {
                showSuccess('Tạo loại bình luận thành công', 'Thành công');
                setShowTypeModal(false);
                setNewTypeName('');
                setNewTypeDescription('');
                loadCommentObjectTypes();
            }
        } catch (error) {
            showError('Không thể tạo loại bình luận', 'Lỗi');
        } finally {
            setLoadingAction(false);
        }
    };

    const handleDeleteComment = async (id: number) => {
        if (!confirm('Bạn có chắc chắn muốn xóa bình luận này?')) return;

        try {
            setLoadingAction(true);
            const response = await commentsService.deleteComment(id);
            if (response.success) {
                showSuccess('Xóa bình luận thành công', 'Thành công');
                loadComments();
            }
        } catch (error) {
            showError('Không thể xóa bình luận', 'Lỗi');
        } finally {
            setLoadingAction(false);
        }
    };

    const handleUpdateCommentStatus = async (id: number, status: string) => {
        try {
            setLoadingAction(true);
            const response = await commentsService.updateComment(id, { status });
            if (response.success) {
                showSuccess('Cập nhật trạng thái thành công', 'Thành công');
                loadComments();
            }
        } catch (error) {
            showError('Không thể cập nhật trạng thái', 'Lỗi');
        } finally {
            setLoadingAction(false);
        }
    };

    const handlePageChange = (newPage: number) => {
        setFilters((prev) => ({ ...prev, page: newPage }));
    };

    const resetFilters = () => {
        setFilters({
            page: 1,
            limit: 10,
            status: '',
            comment_object_type_id: undefined,
            search: '',
            start_date: '',
            end_date: '',
        });
    };

    return (
        <LayoutDefault onClose={onClose} isSidebarOpen={isSidebarOpen}>
            <div className="p-6">
                {/* Header */}
                <div className="mb-6 flex items-start justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800 mb-2">Thống kê bình luận</h1>
                        <p className="text-gray-600">Thống kê và quản lý bình luận của người dùng trong dự án</p>
                    </div>
                    <Button
                        variant="ghost"
                        onClick={onClose}
                        title="Đóng"
                        className="p-2"
                    >
                        <Close />
                    </Button>
                </div>

                {/* Tabs */}
                <Tabs
                    activeTab={activeTab}
                    onChange={setActiveTab}
                    tabs={[
                        { key: 'statistics', label: 'Thống kê', icon: <Message /> },
                        { key: 'comments', label: 'Danh sách bình luận', icon: <Search /> },
                        { key: 'types', label: 'Loại bình luận', icon: <Filter /> },
                    ]}
                />

                {/* Statistics Tab */}
                {activeTab === 'statistics' && (
                    <div>
                        {/* Filters */}
                        <div className="bg-white rounded-lg shadow p-4 mb-6">
                            <h3 className="font-semibold mb-3 flex items-center gap-2">
                                <Filter /> Bộ lọc
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Từ ngày</label>
                                    <Input type="date" value={filters.start_date} onChange={(e) => setFilters((prev) => ({ ...prev, start_date: e.target.value }))} className="w-full" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Đến ngày</label>
                                    <Input type="date" value={filters.end_date} onChange={(e) => setFilters((prev) => ({ ...prev, end_date: e.target.value }))} className="w-full" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Loại bình luận</label>
                                    <Select value={filters.comment_object_type_id || ''} onChange={(e) => setFilters((prev) => ({ ...prev, comment_object_type_id: e.target.value ? Number(e.target.value) : undefined }))} className="w-full">
                                        <option value="">Tất cả</option>
                                        {commentObjectTypes.map((type) => (
                                            <option key={type.id} value={type.id}>
                                                {type.comment_object_type_name}
                                            </option>
                                        ))}
                                    </Select>
                                </div>
                                <div className="flex items-end gap-2">
                                    <Button onClick={loadStatistics} className="flex-1">
                                        <Reload /> Tải lại
                                    </Button>
                                    <Button onClick={resetFilters} variant="outline">
                                        Đặt lại
                                    </Button>
                                </div>
                            </div>
                        </div>

                        {/* Statistics Cards */}
                        {loadingStats ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                                <span className="ml-3 text-gray-600">Đang tải thống kê...</span>
                            </div>
                        ) : statistics ? (
                            <>
                                {/* Charts/Tables */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Comments by Type - Only show when not filtering by specific type */}
                                    {!filters.comment_object_type_id && (
                                        <div className="bg-white rounded-lg shadow p-6">
                                            <h3 className="text-lg font-semibold mb-4">Bình luận theo loại</h3>
                                            <div className="space-y-2">
                                                {statistics.comments_by_type?.length > 0 ? (
                                                    statistics.comments_by_type.map((item: any, index: number) => (
                                                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                                                            <span className="font-medium">{item.type_name}</span>
                                                            <span className="text-blue-600 font-bold">{item.count}</span>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <p className="text-gray-500 text-center py-4">Chưa có dữ liệu</p>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Top Users */}
                                    <div className="bg-white rounded-lg shadow p-6">
                                        <h3 className="text-lg font-semibold mb-4">Người dùng hoạt động nhất</h3>
                                        <div className="space-y-2">
                                            {statistics.top_users?.length > 0 ? (
                                                statistics.top_users.map((user: any, index: number) => (
                                                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                                                        <span className="font-medium">{user.user_name || `User ${user.user_id}`}</span>
                                                        <span className="text-green-600 font-bold">{user.comment_count} bình luận</span>
                                                    </div>
                                                ))
                                            ) : (
                                                <p className="text-gray-500 text-center py-4">Chưa có dữ liệu</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Comments by Date */}
                                    <div className={`bg-white rounded-lg shadow p-6 ${!filters.comment_object_type_id ? 'md:col-span-2' : ''}`}>
                                        <h3 className="text-lg font-semibold mb-4">Bình luận theo ngày</h3>
                                        <div className="overflow-x-auto">
                                            <table className="w-full">
                                                <thead>
                                                    <tr className="border-b">
                                                        <th className="text-left p-2">Ngày</th>
                                                        <th className="text-right p-2">Số lượng</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {statistics.comments_by_date?.map((item: any, index: number) => (
                                                        <tr key={index} className="border-b hover:bg-gray-50">
                                                            <td className="p-2">{new Date(item.date).toLocaleDateString('vi-VN')}</td>
                                                            <td className="text-right p-2 font-semibold text-blue-600">{item.count}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="bg-white rounded-lg shadow p-12 text-center">
                                <Message className="mx-auto text-gray-400 text-5xl mb-4" />
                                <p className="text-gray-500">Không có dữ liệu thống kê</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Comments List Tab */}
                {activeTab === 'comments' && (
                    <div>
                        {/* Filters */}
                        <div className="bg-white rounded-lg shadow p-4 mb-6">
                            <h3 className="font-semibold mb-3 flex items-center gap-2">
                                <Filter /> Bộ lọc
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Loại</label>
                                    <Select value={filters.comment_object_type_id || ''} onChange={(e) => setFilters((prev) => ({ ...prev, comment_object_type_id: e.target.value ? Number(e.target.value) : undefined, page: 1 }))} className="w-full">
                                        <option value="">Tất cả</option>
                                        {commentObjectTypes.map((type) => (
                                            <option key={type.id} value={type.id}>
                                                {type.comment_object_type_name}
                                            </option>
                                        ))}
                                    </Select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Từ ngày</label>
                                    <Input type="date" value={filters.start_date} onChange={(e) => setFilters((prev) => ({ ...prev, start_date: e.target.value, page: 1 }))} className="w-full" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Đến ngày</label>
                                    <Input type="date" value={filters.end_date} onChange={(e) => setFilters((prev) => ({ ...prev, end_date: e.target.value, page: 1 }))} className="w-full" />
                                </div>
                                <div className="flex items-end">
                                    <Button onClick={resetFilters} variant="outline" className="w-full">
                                        Đặt lại
                                    </Button>
                                </div>
                            </div>
                        </div>

                        {/* Comments Table */}
                        <div className="bg-white rounded-lg shadow overflow-hidden">
                            {loadingComments ? (
                                <div className="flex items-center justify-center py-12">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                                    <span className="ml-3 text-gray-600">Đang tải bình luận...</span>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Người dùng</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nội dung</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Đánh giá</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Loại</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Đối tượng</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ngày tạo</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Thao tác</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {comments.map((comment) => (
                                                <tr key={comment.id} className="hover:bg-gray-50">
                                                    <td className="px-4 py-3 text-sm">{comment.id}</td>
                                                    <td className="px-4 py-3 text-sm">{comment.created_by_name || `User ${comment.created_by}`}</td>
                                                    <td className="px-4 py-3 text-sm max-w-xs truncate">{comment.comment_content}</td>
                                                    <td className="px-4 py-3 text-sm">{comment.star ? <span className="text-yellow-500">{'⭐'.repeat(comment.star)}</span> : <span className="text-gray-400">-</span>}</td>
                                                    <td className="px-4 py-3 text-sm">{comment.comment_object_type_name}</td>
                                                    <td className="px-4 py-3 text-sm">
                                                        {comment.scene_title ? (
                                                            <div>
                                                                <div className="font-medium">{comment.scene_title}</div>
                                                            </div>
                                                        ) : (
                                                            comment.object_id
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm">{new Date(comment.created_at).toLocaleDateString('vi-VN')}</td>
                                                    <td className="px-4 py-3 text-sm">
                                                        <div className="flex items-center gap-2">
                                                            <button onClick={() => handleDeleteComment(comment.id)} className="text-red-600 hover:text-red-800" title="Xóa">
                                                                <Delete />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    {comments.length === 0 && !loadingComments && (
                                        <div className="p-12 text-center">
                                            <Message className="mx-auto text-gray-400 text-5xl mb-4" />
                                            <p className="text-gray-500">Không có bình luận nào</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Pagination */}
                            {pagination.totalPages > 1 && (
                                <div className="flex items-center justify-between px-4 py-3 border-t">
                                    <div className="text-sm text-gray-700">
                                        Hiển thị {(pagination.page - 1) * pagination.limit + 1} đến {Math.min(pagination.page * pagination.limit, pagination.total)} trong tổng số {pagination.total} bình luận
                                    </div>
                                    <div className="flex gap-2">
                                        <Button onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page === 1} variant="outline">
                                            Trước
                                        </Button>
                                        <div className="flex items-center gap-1">
                                            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                                                .filter((page) => page === 1 || page === pagination.totalPages || Math.abs(page - pagination.page) <= 1)
                                                .map((page, index, array) => (
                                                    <div key={page}>
                                                        {index > 0 && array[index - 1] !== page - 1 && <span className="px-2">...</span>}
                                                        <Button onClick={() => handlePageChange(page)} variant={page === pagination.page ? 'default' : 'outline'} className="min-w-[40px]">
                                                            {page}
                                                        </Button>
                                                    </div>
                                                ))}
                                        </div>
                                        <Button onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page === pagination.totalPages} variant="outline">
                                            Sau
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Comment Object Types Tab */}
                {activeTab === 'types' && (
                    <div>
                        <div className="mb-4 flex justify-between items-center">
                            <h2 className="text-xl font-semibold">Danh sách loại bình luận</h2>
                            <Button onClick={() => setShowTypeModal(true)}>
                                <Plus /> Thêm loại mới
                            </Button>
                        </div>

                        <div className="bg-white rounded-lg shadow overflow-hidden">
                            {loadingTypes ? (
                                <div className="flex items-center justify-center py-12">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                                    <span className="ml-3 text-gray-600">Đang tải loại bình luận...</span>
                                </div>
                            ) : (
                                <>
                                    <table className="w-full">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tên</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mô tả</th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ngày tạo</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {commentObjectTypes.map((type) => (
                                                <tr key={type.id} className="hover:bg-gray-50">
                                                    <td className="px-4 py-3 text-sm">{type.id}</td>
                                                    <td className="px-4 py-3 text-sm font-medium">{type.comment_object_type_name}</td>
                                                    <td className="px-4 py-3 text-sm">{type.description || '-'}</td>
                                                    <td className="px-4 py-3 text-sm">{type.created_at ? new Date(type.created_at).toLocaleDateString('vi-VN') : '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    {commentObjectTypes.length === 0 && (
                                        <div className="p-12 text-center">
                                            <Filter className="mx-auto text-gray-400 text-5xl mb-4" />
                                            <p className="text-gray-500">Chưa có loại bình luận nào</p>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* Action Loading Overlay (only for create/update/delete) */}
                {loadingAction && (
                    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="mt-4 text-gray-600">Đang xử lý...</p>
                        </div>
                    </div>
                )}

                {/* Create Comment Object Type Modal */}
                <Modal
                    isOpen={showTypeModal}
                    onClose={() => {
                        setShowTypeModal(false);
                        setNewTypeName('');
                        setNewTypeDescription('');
                    }}
                    title="Thêm loại bình luận mới"
                >
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium mb-1">
                                Tên loại <span className="text-red-500">*</span>
                            </label>
                            <Input type="text" placeholder="Nhập tên loại bình luận" value={newTypeName} onChange={(e) => setNewTypeName(e.target.value)} className="w-full" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Mô tả</label>
                            <Textarea placeholder="Nhập mô tả (tùy chọn)" value={newTypeDescription} onChange={(e) => setNewTypeDescription(e.target.value)} className="w-full" rows={3} />
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button
                                onClick={() => {
                                    setShowTypeModal(false);
                                    setNewTypeName('');
                                    setNewTypeDescription('');
                                }}
                                variant="outline"
                            >
                                Hủy
                            </Button>
                            <Button onClick={handleCreateCommentObjectType} disabled={loadingAction}>
                                {loadingAction ? 'Đang tạo...' : 'Tạo mới'}
                            </Button>
                        </div>
                    </div>
                </Modal>

                {/* View Comment Detail Modal */}
                <Modal
                    isOpen={showCommentModal}
                    onClose={() => {
                        setShowCommentModal(false);
                        setSelectedComment(null);
                    }}
                    title="Chi tiết bình luận"
                >
                    {selectedComment && (
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-600 mb-1">ID</label>
                                <p className="text-gray-900">{selectedComment.id}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-600 mb-1">Người dùng</label>
                                <p className="text-gray-900">{selectedComment.created_by_name || `User ${selectedComment.created_by}`}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-600 mb-1">Loại bình luận</label>
                                <p className="text-gray-900">{selectedComment.comment_object_type_name}</p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-600 mb-1">Đối tượng</label>
                                {selectedComment.scene_title ? (
                                    <div>
                                        <p className="text-gray-900 font-medium">{selectedComment.scene_title}</p>
                                    </div>
                                ) : (
                                    <p className="text-gray-900">{selectedComment.object_id}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-600 mb-1">Nội dung</label>
                                <div className="p-3 bg-gray-50 rounded border">
                                    <p className="text-gray-900 whitespace-pre-wrap">{selectedComment.comment_content}</p>
                                </div>
                            </div>
                            {selectedComment.comment_attachment && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-1">Tệp đính kèm</label>
                                    <p className="text-blue-600">{selectedComment.comment_attachment}</p>
                                </div>
                            )}
                            <div>
                                <label className="block text-sm font-medium text-gray-600 mb-1">Đánh giá</label>
                                <div className="text-2xl">{selectedComment.star ? <span className="text-yellow-500">{'⭐'.repeat(selectedComment.star)}</span> : <span className="text-gray-400">Chưa đánh giá</span>}</div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-1">Ngày tạo</label>
                                    <p className="text-gray-900">{new Date(selectedComment.created_at).toLocaleString('vi-VN')}</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-600 mb-1">Cập nhật lần cuối</label>
                                    <p className="text-gray-900">{selectedComment.updated_at ? new Date(selectedComment.updated_at).toLocaleString('vi-VN') : '-'}</p>
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 pt-4 border-t">
                                <Button
                                    onClick={() => {
                                        handleDeleteComment(selectedComment.id);
                                        setShowCommentModal(false);
                                    }}
                                    variant="outline"
                                    className="text-red-600 border-red-600 hover:bg-red-50"
                                >
                                    Xóa
                                </Button>
                                <Button onClick={() => setShowCommentModal(false)} variant="outline">
                                    Đóng
                                </Button>
                            </div>
                        </div>
                    )}
                </Modal>
            </div>
        </LayoutDefault>
    );
}
